import React, { useEffect, useState } from 'react';
import { downscaleReferenceImage } from '../../api/client';
import { SceneState, useSceneStore } from '../../state/sceneStore';
import {
  dismissReferenceImageDownscalePrompt,
  isReferenceImageDownscaleDismissed,
  RECOMMENDED_MAX_TEXTURE_DIMENSION,
  referenceImageExceedsRecommendedSize,
  referenceImageMaxDimension,
} from '../../utils/referenceImageLimits';

interface ReferenceImageDownscalePromptProps {
  width: number;
  height: number;
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15, 23, 42, 0.75)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};

const panelStyle: React.CSSProperties = {
  background: '#1e293b',
  color: 'white',
  borderRadius: '8px',
  border: '1px solid #334155',
  padding: '20px',
  maxWidth: '460px',
  width: '90%',
};

const primaryButtonStyle: React.CSSProperties = {
  background: '#4f46e5',
  color: 'white',
  border: 'none',
  borderRadius: '6px',
  padding: '8px 12px',
  cursor: 'pointer',
  fontSize: '13px',
  fontWeight: '500',
  flex: 1,
};

const secondaryButtonStyle: React.CSSProperties = {
  ...primaryButtonStyle,
  background: '#334155',
};

const ReferenceImageDownscalePrompt: React.FC<ReferenceImageDownscalePromptProps> = ({
  width,
  height,
}) => {
  const currentProject = useSceneStore((state: SceneState) => state.currentProject);
  const updateProjectReferenceImage = useSceneStore((state: SceneState) => state.updateProjectReferenceImage);
  const [isDownscaling, setIsDownscaling] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isDismissed, setIsDismissed] = useState(false);

  const imageUrl = currentProject?.reference_image_url;
  const projectId = currentProject?.id;

  useEffect(() => {
    setErrorMessage(null);
    setIsDismissed(false);
  }, [imageUrl, width, height]);

  if (!projectId || !imageUrl) {
    return null;
  }

  if (!referenceImageExceedsRecommendedSize(width, height)) {
    return null;
  }

  if (isDismissed || isReferenceImageDownscaleDismissed(projectId, imageUrl)) {
    return null;
  }

  const maxDimension = referenceImageMaxDimension(width, height);

  const handleDismiss = () => {
    dismissReferenceImageDownscalePrompt(projectId, imageUrl);
    setIsDismissed(true);
  };

  const handleDownscale = async () => {
    setIsDownscaling(true);
    setErrorMessage(null);
    try {
      const updatedProject = await downscaleReferenceImage(
        projectId,
        RECOMMENDED_MAX_TEXTURE_DIMENSION,
      );
      if (updatedProject.reference_image_url) {
        updateProjectReferenceImage(
          updatedProject.reference_image_url,
          updatedProject.reference_image_original_filename,
        );
      }
      setIsDismissed(true);
    } catch (error) {
      console.error('Failed to downscale reference image:', error);
      const detail =
        typeof error === 'object'
        && error !== null
        && 'response' in error
        && typeof (error as { response?: { data?: { detail?: string } } }).response?.data?.detail === 'string'
          ? (error as { response: { data: { detail: string } } }).response.data.detail
          : null;
      setErrorMessage(
        detail ?? 'Failed to downscale the reference image. You can try again or keep the current size.',
      );
    } finally {
      setIsDownscaling(false);
    }
  };

  return (
    <div style={overlayStyle} data-testid="reference-image-downscale-modal">
      <div style={panelStyle}>
        <h4 style={{ marginTop: 0 }}>Large reference image</h4>
        <p style={{ fontSize: '14px', lineHeight: 1.5, color: '#cbd5e1' }}>
          This reference image is {width}×{height} pixels (max edge {maxDimension}px). Images above{' '}
          {RECOMMENDED_MAX_TEXTURE_DIMENSION}px can make the scene slower to load and interact with.
          Scale it down to {RECOMMENDED_MAX_TEXTURE_DIMENSION}px on the longest edge?
        </p>
        {errorMessage && (
          <p style={{ fontSize: '13px', color: '#fca5a5', marginBottom: 0 }}>{errorMessage}</p>
        )}
        <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
          <button
            type="button"
            style={primaryButtonStyle}
            data-testid="reference-image-downscale-confirm"
            disabled={isDownscaling}
            onClick={() => void handleDownscale()}
          >
            {isDownscaling ? 'Scaling...' : `Scale to ${RECOMMENDED_MAX_TEXTURE_DIMENSION}px`}
          </button>
          <button
            type="button"
            style={secondaryButtonStyle}
            data-testid="reference-image-downscale-dismiss"
            disabled={isDownscaling}
            onClick={handleDismiss}
          >
            Keep current size
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReferenceImageDownscalePrompt;
